import {NoOperation} from '@/library/utils.ts';
import store from '@/store/index.ts';
import axios from 'axios';
import cssVars from 'css-vars-ponyfill';
import {popupDefaultCancelButton} from '../library/popup';
import Loader from './../library/intranet/loader';
import TopMessageHandler from './../library/topMessageHandler';

export const XHR_STATUS_DEFAULT = 'OK';
export const XHR_STATUS_WARNING = 'WARNING';
export const XHR_STATUS_ERROR   = 'ERROR';
export const XHR_STATUS_INVALID = 'INVALID';

export const ERROR_ACCESS_VIOLATION   = 'error-unauthorized-access';
export const ERROR_MAINTENANCE_ACTIVE = 'error-maintenance-active';
export const ERROR_LOGIN_REQUIRED     = 'login-required';
export const ERROR_AJAX               = 'error-ajax';
export const ERROR_RUNTIME            = 'error-runtime';
export const ERROR_EDIT_BLOCKED       = 'error-edit-blocked';

export function getErrorType(error)
{
    if (error.response && error.response.data && error.response.data && error.response.data.error)
    {
        return error.response.data.error;
    }
    else if (error.data && error.data && error.data.error)
    {
        return error.data.error;
    }

    return '';
}

/**
 * get a login referrer route from given route
 *
 * @param route
 * @returns {{name: string, replace: boolean}|{name: string, replace: boolean, params: {referrer: string}}}
 */
export function getLoginReferrer(route)
{
    if ('string' === typeof route)
    {
        return {
            name: 'login-referrer',
            replace: true,
            params: {
                referrer: btoa(route),
            },
        };
    }

    route = route.value ? route.value : route;

    if (route.name === 'logout' || (route.meta && route.meta.layout === 'minimal'))
    {
        return {
            name: 'login',
            replace: true,
        };
    }

    return {
        name: 'login-referrer',
        replace: true,
        params: {
            referrer: btoa(route.path),
        },
    };
}

const POPUP_REQUEST_DEFAULT = {
    delete: {
        headline: 'Eintrag löschen',
        message: 'Sind Sie sicher, dass Sie den Eintrag löschen möchten?',
        success: 'Der Eintrag wurde erfolgreich gelöscht.',
        failed: 'Der Eintrag konnte nicht gelöscht werden.',
        loader: 'Eintrag wird gelöscht...',
    },
    confirm: {
        headline: 'Bestätigung erforderlich',
    },
    deleteFile: {
        headline: 'Datei löschen',
        message: 'Sind Sie sicher, dass Sie die Datei löschen möchten?',
        success: 'Die Datei wurde erfolgreich gelöscht.',
        failed: 'Die Datei konnte nicht gelöscht werden.',
        loader: 'Datei wird gelöscht...',
    },
    edit: {
        headline: 'Eintrag bearbeiten',
        message: 'Sind Sie sicher, dass Sie diesen Eintrag bearbeiten möchten?',
        success: 'Der Eintrag wurde erfolgreich bearbeitet.',
        failed: 'Der Eintrag konnte nicht bearbeitet werden.',
        loader: 'Eintrag wird bearbeitet...',
    },
    changeStatusActivate: {
        headline: 'Eintrag aktivieren',
        message: 'Sind Sie sicher, dass Sie den diesen Eintrag aktivieren möchten?',
        success: 'Der Eintrag wurde erfolgreich aktiviert.',
        failed: 'Der Eintrag konnte nicht aktiviert werden.',
        loader: 'Eintrag wird bearbeitet...',
    },
    changeStatusDeactivate: {
        headline: 'Eintrag deaktivieren',
        message: 'Sind Sie sicher, dass Sie den diesen Eintrag deaktivieren möchten?',
        success: 'Der Eintrag wurde erfolgreich deaktiviert.',
        failed: 'Der Eintrag konnte nicht Activate werden.',
        loader: 'Eintrag wird bearbeitet...',
    },
};

export function sendPopupRequest({
    url,
    message = {},
    type = 'delete',
    data = {},
    options = {},
})
{
    message = Object.assign({}, POPUP_REQUEST_DEFAULT[type], message || {});

    return new Promise((resolve, reject) =>
    {
        askPopupRequest({
            message,
            type,
            options,
        }).then(() =>
        {
            Loader.show(message.loader);
            axios.post(url, data).then((response) =>
            {
                if (200 === response.status && XHR_STATUS_DEFAULT === response.data.status)
                {
                    if (options.showResponseMessage !== false)
                    {
                        if (response.data.message && options.sendResponseMessage !== false)
                        {
                            TopMessageHandler.show(response.data.message, 'success');
                        }
                        else if (message.success)
                        {
                            TopMessageHandler.show(message.success, 'success');
                        }
                    }

                    resolve(response);
                }
                else
                {
                    TopMessageHandler.show(response.data.message || message.failed, 'error');
                    reject(response);
                }
            }).catch((error) =>
            {
                errorHandler(error);
                reject(error);
            }).finally(() =>
            {
                Loader.hide();
            });
        })
        .catch(reject);
    });
}

export function askPopupRequest({
    message = {},
    type = 'delete',
    options = {},
})
{
    message = Object.assign({}, POPUP_REQUEST_DEFAULT[type], message || {});

    return new Promise((resolve, reject) =>
    {
        let popupOptions = {
            title: message.headline,
            text: message.message,
            size: 'small',
            buttons: [
                Object.assign({}, popupDefaultCancelButton, {
                    cmd(popup, event)
                    {
                        reject('popupClosed');
                        popup.close(event);
                    },
                }, options.buttonCancel || {}),
                Object.assign({
                    title: 'Okay',
                    icon: 'check',
                    type: 'success',
                    cmd(popup, event)
                    {
                        resolve();
                        popup.close();
                    },
                }, options.buttonOkay || {}),
            ],
        };

        for (let prop of ['textIcon', 'titleIcon', 'textIconClass', 'component', 'componentProps', 'events', 'size'])
        {
            if (typeof options[prop] !== 'undefined')
            {
                popupOptions[prop] = options[prop];
            }
        }

        store.dispatch('createPopup', popupOptions).then(popup => popup.open().then(NoOperation));
    });
}

export function popupClosedReasonCheck(reason)
{
    if (reason === 'popupClosed')
    {
        return true;
    }

    throw reason;
}

export function setCssVars(variables = {})
{
    cssVars({
        onlyLegacy: true,
        updateURLs: false,
        watch: true,
        variables,
    });
}

export function setCssColorVars(colors = {})
{
    let variables = Object.assign({}, colors);

    for (let key in colors)
    {
        if (colors.hasOwnProperty(key) && !key.indexOf('color-'))
        {
            variables[key.replace('color-', 'rgb-color-')] = 'rgb(' + colors[key] + ')';
        }
    }

    setCssVars(variables);
}

export function isStaging()
{
    const url = window.location.host;

    return !!(url.match(/^intranet.portuna.de/gi)
        || url.match(/^steffi-test.mein-intra.net/gi)
        || url.match(/^installer.mein-intra.net/gi)
        || url.match(/^(dev)(.+)(mein-intra.net)/)
        || url.match(/^(korrekturen-)(.+)(mein-intra.net)/));
}

export function isDeveloper()
{
    let ua = navigator.userAgent || '';

    return ua.includes('f645f118e1ed5824f57251e898d8c3a2+');
}

export function errorHandler(err, showMessage = true, fallbackMessage = '')
{
    let message;

    if (typeof err === 'object' && err)
    {
        if (err.response && err.response.data.message)
        {
            message = err.response.data.message;
        }
        else if (err.data && typeof err.data === 'object' && err.data.message)
        {
            message = err.data.message;
        }
    }

    let isResponseMessage = !!message;
    let defaultMessage    = fallbackMessage || 'Es ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut.';

    message = message || defaultMessage;

    if (err instanceof Error || isResponseMessage)
    {
        err && console.error('[errorHandler]', err);
        showMessage && TopMessageHandler.error(message);

        return 'string' === typeof message ? message : defaultMessage;
    }

    if (typeof err === 'string')
    {
        message = err;
    }
    else if (Array.isArray(err) && typeof err[0] === 'string')
    {
        message = err.message.join('<br>');
    }
    else if (typeof err === 'object' && err)
    {
        if (err.message)
        {
            message = err.message;

            if (Array.isArray(err.message))
            {
                message = err.message.join('<br>');
            }
        }
    }

    if (message !== null)
    {
        if (!message || typeof message !== 'string')
        {
            console.debug('[errorHandler] wrong data type for "err":', err);

            message = defaultMessage;
        }

        if ('popupClosed' !== message)
        {
            showMessage && TopMessageHandler.error(message);
        }
    }
    else
    {
        console.debug('[errorHandler] wrong data type for "err":', err);
    }

    return 'string' === typeof message ? message : defaultMessage;
}

export function urlHelper(params = {}, complete = false)
{
    let url   = (complete ? 'https://' + window.location.hostname : '') + '/',
        parts = [];

    for (let prop in params)
    {
        if (params.hasOwnProperty(prop))
        {
            if (prop !== 'resource' && prop !== 'controller' && prop !== 'action')
            {
                parts.push(prop);
            }

            parts.push(params[prop]);
        }
    }

    return url + parts.join('/');
}

export function parseParams(params = {})
{
    let url = '';

    if (params)
    {
        for (let paramName in params)
        {
            if (params.hasOwnProperty(paramName))
            {
                url += '/' + paramName + '/' + encodeURIComponent(params[paramName]);
            }
        }
    }

    return url;
}

export const REGEX_RANGE_FILTER         = 'today|next_month|next_year|all_future|last_month|last_year|all_past|all|individual';
export const ROUTE_REGEX_BASE64_URL     = '[\\w\\d\\=\\-\\+\\_]+';
export const ROUTE_REF_PREFIX           = '!';
export const ROUTE_REF_REGEX            = ROUTE_REF_PREFIX + ROUTE_REGEX_BASE64_URL;
export const ROUTE_REF_PARAM_DEFINITION = ':ref(' + ROUTE_REF_REGEX + ')?';
export const ROUTE_REF_PARAM_DEFINITION_SLASH = '/' + ROUTE_REF_PARAM_DEFINITION;

export function parseReferrer(ref)
{
    return atob(ref.replace('!', ''));
}

export function base64UrlEncode(string)
{
    return btoa(string).replace('+', '-').replace('/', '_');
}

export function base64UrlDecode(input)
{
    input = input.replace(/-/g, '+').replace(/_/g, '/');

    const pad = input.length % 4;
    if(pad) {
        if(1 === pad) {
            throw new Error('InvalidLengthError: Input base64url string is the wrong length to determine padding');
        }
        input += new Array(5-pad).join('=');
    }

    return atob(input);
}

const externalRegExp = /^https?:\/\/|^\/\//i;

export function isExternalLink(link)
{
    return externalRegExp.test(link);
}

export function deepCopyObject(object)
{
    return JSON.parse(JSON.stringify(object));
}

export function hasTextContent(text)
{
    return !!text.replace(/\s|&nbsp;/g, '').replace(/<\/?\w+ ?[^>]*>/g, '');
}